import {
  EqualsIcon,
  ScalesIcon,
  TrendDownIcon,
  TrendUpIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  MARKET_EVALUATION_FIELD,
  formatSignedMoney,
  formatSignedPercent,
  marketNumbers,
  marketScalePosition,
} from './analytics';
import { formatCalendarDate, formatPrice } from './lib';
import { ProvenanceInfo } from './ProvenanceInfo';
import type { MarketComparisonData, VehiclePrice, VehicleSource } from './types';
import styles from './MarketComparison.module.css';

interface MarketComparisonProps {
  vehicleId: string;
  price: VehiclePrice;
  market: MarketComparisonData;
  source?: VehicleSource | null | undefined;
}

/**
 * Сравнение цены с рыночной медианой (FE-060, MVP_PRD §16).
 *
 * Показывает цену автомобиля, медиану, разницу в евро и процентах, размер
 * выборки, дату снимка и уровень качества сравнения. Четыре состояния приходят
 * с backend и здесь только раскладываются по разметке:
 *
 * * `available` — числа как есть;
 * * `stale` — снимку 31–90 дней: числа остаются, но с явным предупреждением;
 * * `unavailable` — все подходящие выборки старше 90 дней, и **числовое
 *   сравнение не показывается вовсе**;
 * * `insufficient_data` — сопоставимой выборки нет.
 *
 * Отклонение цены показывается синим, цветом информационного расчёта, а не
 * зелёным «выгодно»: цена ниже медианы сама по себе не преимущество, сильное
 * отклонение вниз поднимает признак внимания, а не похвалу (MVP_PRD §13).
 *
 * Оговорку о границах платформы («анализируются только данные каталога») несёт
 * дисклеймер в подвале — он есть на каждой странице (MVP_PRD §5). Дублировать её
 * под каждым блоком не нужно: пользователю нужны числа и их основание, а не
 * рассказ о том, чего система не делает.
 */
export function MarketComparison({ vehicleId, price, market, source }: MarketComparisonProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'de';
  const numbers = marketNumbers(market);

  const info = (
    <ProvenanceInfo
      vehicleId={vehicleId}
      fields={[MARKET_EVALUATION_FIELD]}
      subject={t('vehicle.market.title')}
      {...(source ? { source: { name: source.name, url: source.url } } : {})}
    />
  );

  // Числа без основания не показываются: если backend не прислал полный набор
  // (медиана, выборка, дата снимка), блок ведёт себя как «недостаточно данных».
  if (numbers === null) {
    return (
      <MarketShell info={info}>
        <p className={styles.stateTitle}>
          {market.status === 'unavailable'
            ? t('vehicle.market.unavailable.title')
            : t('vehicle.market.insufficient.title')}
        </p>
        <p className={styles.stateText}>
          {market.status === 'unavailable'
            ? t('vehicle.market.unavailable.text')
            : t('vehicle.market.insufficient.text')}
        </p>
      </MarketShell>
    );
  }

  const snapshot = formatCalendarDate(numbers.snapshotDate, locale) ?? numbers.snapshotDate;
  const DirectionIcon =
    numbers.direction === 'below'
      ? TrendDownIcon
      : numbers.direction === 'above'
        ? TrendUpIcon
        : EqualsIcon;

  return (
    <MarketShell info={info}>
      <div className={styles.headline}>
        <p className={`${styles.delta} tabular`}>
          <DirectionIcon size={26} weight="bold" aria-hidden="true" />
          {formatSignedPercent(numbers.deltaPercent, locale)}
        </p>
        <p className={styles.deltaLabel}>
          {t(`vehicle.market.direction.${numbers.direction}`)}
          <span className={`${styles.deltaMoney} tabular`}>
            {formatSignedMoney(numbers.deltaCents, price.currency, locale)}
          </span>
        </p>
      </div>

      {/*
        Шкала показывает направление и порядок отклонения, а не распределение
        цен: в данных есть только медиана и размер выборки, гистограммы нет.
        Поэтому подписаны обе опорные точки, а не нарисована «кривая рынка».
      */}
      <div className={styles.scale}>
        <div className={styles.track} aria-hidden="true">
          <span className={styles.median} />
          <span
            className={styles.marker}
            style={
              {
                '--position': `${marketScalePosition(numbers.deltaPercent)}%`,
              } as React.CSSProperties
            }
          />
        </div>
        <dl className={styles.anchors}>
          <div>
            <dt>{t('vehicle.market.vehiclePrice')}</dt>
            <dd className={`${styles.anchorValue} tabular`}>{formatPrice(price, locale)}</dd>
          </div>
          <div className={styles.anchorCenter}>
            <dt>{t('vehicle.market.median')}</dt>
            <dd className={`${styles.anchorValue} tabular`}>
              {formatPrice(numbers.median, locale)}
            </dd>
          </div>
        </dl>
      </div>

      {market.status === 'stale' ? (
        <p className={styles.stale} role="note">
          <WarningIcon size={18} weight="fill" aria-hidden="true" />
          {t('vehicle.market.stale', { date: snapshot })}
        </p>
      ) : null}

      <dl className={styles.meta}>
        <div>
          <dt>{t('vehicle.market.sampleLabel')}</dt>
          <dd className="tabular">
            {t('vehicle.market.sampleValue', { count: numbers.sampleSize })}
          </dd>
        </div>
        <div>
          <dt>{t('vehicle.market.snapshotLabel')}</dt>
          <dd className="tabular">{snapshot}</dd>
        </div>
        <div>
          <dt>{t('vehicle.market.qualityLabel')}</dt>
          <dd>
            {market.match_level === null
              ? t('catalog.noData')
              : t('vehicle.market.qualityValue', {
                  level: market.match_level,
                  score: Math.round(market.confidence_score),
                })}
          </dd>
        </div>
      </dl>

      {market.match_level ? (
        <p className={styles.levelNote}>{t(`vehicle.market.level.${market.match_level}`)}</p>
      ) : null}
    </MarketShell>
  );
}

/**
 * Внешняя обойма блока: заголовок, кнопка происхождения и вложенное ядро.
 * Двойная оправа с концентрическими радиусами — тот же приём, что и в модалке.
 */
function MarketShell({ info, children }: { info: ReactNode; children: ReactNode }) {
  const { t } = useTranslation();
  // Идентификатор генерируется, а не пишется руками: блок должен оставаться
  // корректным и там, где на странице несколько автомобилей (сравнение).
  const titleId = useId();

  return (
    <section className={styles.shell} aria-labelledby={titleId}>
      <div className={styles.core}>
        <header className={styles.header}>
          <div className={styles.heading}>
            <span className={styles.headerIcon} aria-hidden="true">
              <ScalesIcon size={18} weight="bold" />
            </span>
            <h2 id={titleId} className={styles.title}>
              {t('vehicle.market.title')}
            </h2>
          </div>
          {info}
        </header>
        {children}
      </div>
    </section>
  );
}
